import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Form, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import API from '../../../utils/API';
import Table from '../../../components/Table/Table';
import { connect } from 'react-redux';
import constants from '../../../utils/constants';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import useWebSocket from '../../../components/common/useWebSocket';
import ChevronDown from '../../../assets/icons/common/chevron-down.svg';
import ChevronRight from '../../../assets/icons/common/chevron-right.svg';
import AssignBuildingModal from './AssignBuildingsModal';
import useWebSocketDataSync from '../../../components/common/useWebSocketDataSync';
import { updateTreeDataMap, createSiteObject, updateSinglewireSiteObject, ensureChildSelectionConsistency } from '../../../utils/treeDataSync';

function SiteMapping(props) {
  const navigate = useNavigate();
  const websocket = useWebSocket();  
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  const [cmses, setCmses] = useState([]);
  const [selectedCms, setSelectedCms] = useState('');
  const [sites, setSites] = useState([]);
  const [childSitesMap, setChildSitesMap] = useState({});
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('startTime');
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allCmsData, setAllCmsData] = useState([]);

  const [singlewireSites, setSinglewireSites] = useState([]);
  const [buildingName, setBuildingName] = useState('');

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  // Initialize dataMap before using it in effects
  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  useEffect(() => {
    if (!permissionIds.includes(constants.permissionIds.aPlusEdit)) {
      navigate('/forbidden');
    }
  }, [navigate, permissionIds]);

  // Replace the WebSocket effect with the hook
  useWebSocketDataSync({
    websocket,
    messageId: 5001,
    fetchLatestData: async () => {
      const res = await API.getCmses();
      setAllCmsData(res.data);
      setCmses(res.data);
      return res.data.find(cms => cms.id === selectedCms?.id);
    },
    processUpdates: async (currentCms) => {
      if (!currentCms?.sites || !selectedCms) return [];
      
      // Store current state before updates
      const currentExpansionState = {};
      
      // Save expansion state of all rows
      Object.keys(dataMap.data).forEach(id => {
        if (dataMap.data[id] && dataMap.data[id].isExpanded) {
          currentExpansionState[id] = true;
        }
      });
      
      // Find new, modified, and deleted sites by comparing with current data
      const updatedSites = [];
      const newSites = [];
      const deletedSiteIds = [];
      
      // First, identify deleted sites by checking which existing sites are no longer in the current data
      if (dataMap.ids.length > 0) {
        // Create a map of all current site IDs for quick lookup
        const currentSiteIds = new Set(currentCms.sites.map(site => site.id));
        
        // Check each site in our dataMap to see if it still exists in the current data
        Object.keys(dataMap.data).forEach(siteId => {
          if (!currentSiteIds.has(siteId)) {
            deletedSiteIds.push(siteId);
          }
        });
      }
      
      // Then identify new and updated sites
      currentCms.sites.forEach(site => {
        const existingSite = dataMap.data[site.id];
        
        if (!existingSite) {
          // This is a new site
          newSites.push(site);
        } else if (existingSite.singlewireSiteName !== site.singlewire_site_name) {
          // This site has been updated (Singlewire site assignment changed)
          updatedSites.push(site);
        }
      });
      
      console.log('Found new sites:', newSites.length, 'Updated sites:', updatedSites.length, 'Deleted sites:', deletedSiteIds.length);
      
      // If we have any changes to apply
      if (newSites.length > 0 || updatedSites.length > 0 || deletedSiteIds.length > 0) {
        // Handle deleted sites first
        if (deletedSiteIds.length > 0) {
          // Remove deleted sites from dataMap
          deletedSiteIds.forEach(siteId => {
            // Remove from selectedIds if selected
            if (dataMap.selectedIds[siteId]) {
              delete dataMap.selectedIds[siteId];
            }
            
            // Remove from ids, sortedIds, and filteredIds arrays
            dataMap.ids = dataMap.ids.filter(id => id !== siteId);
            dataMap.sortedIds = dataMap.sortedIds.filter(id => id !== siteId);
            dataMap.filteredIds = dataMap.filteredIds.filter(id => id !== siteId);
            
            // If this site has a parent, update the parent's children array
            const site = dataMap.data[siteId];
            if (site && site.parentId && dataMap.data[site.parentId]) {
              dataMap.data[site.parentId].children = dataMap.data[site.parentId].children.filter(
                child => child.id !== siteId
              );
              
              // Update parent's hasChildren flag
              dataMap.data[site.parentId].hasChildren = dataMap.data[site.parentId].children.length > 0;
            }
            
            // Finally, delete the site from dataMap.data
            delete dataMap.data[siteId];
          });
        }
        
        // Then handle new and updated sites
        updateTreeDataMap(
          dataMap, 
          currentCms.sites, 
          newSites, 
          updatedSites, 
          currentExpansionState,
          {
            idField: 'id',
            parentIdField: 'parent_id',
            createItemObject: createSiteObject,
            updateItemObject: updateSinglewireSiteObject,
            styleForLevel: (level) => ({
              ...styles.nestedRow(level)
            })
          }
        );
        
        return [...newSites, ...updatedSites, ...deletedSiteIds.map(id => ({ id, deleted: true }))];
      }
      
      return [];
    },
    onSuccess: (changes) => {
      if (changes.length > 0) {
        setSuccess(<FormattedMessage id="SITES_UPDATED" defaultMessage="Sites updated successfully"/>);
        
        // Ensure child selection consistency
        ensureChildSelectionConsistency(dataMap);
        
        // Update the UI with the new data
        sortMap(dataMap);
        setData(generateDataSlice(dataMap, startIndexForTable));
        setDataCount(dataMap.sortedIds.length);
        setTotalDataCount(Object.keys(dataMap.data).length);
      }
    },
    onError: (error) => {
      setError(<FormattedMessage id="SITES_UPDATED_FAILED" defaultMessage="Failed to update sites"/>);
    },
    dependencies: [selectedCms, dataMap, startIndexForTable]
  });

  // Add this effect to fetch CMSes and Singlewire data on component mount
  useEffect(() => {
    Promise.all([
      API.getCmses(),
      API.getSinglewireSites(),
      API.getSinglewireSiteRoles(),
    ])
      .then(([cmsRes, singlewireRes, singlewireSiteRolesRes, singlewireSiteRoleSitesRes]) => {
        setAllCmsData(cmsRes.data);
        setCmses(cmsRes.data);
        console.log('singlewireSiteRolesRes:', singlewireSiteRolesRes.data.siteRoles.data);
        const firstSiteRole = singlewireSiteRolesRes.data.siteRoles.data[0];
        console.log('firstSiteRole:', firstSiteRole);
        API.getSinglewireSiteRoleSites(firstSiteRole.id)
          .then((res) => {
            console.log('singlewireSiteRoleSitesRes:', res.data);
          });
        
        if (singlewireRes.data.success) {
          console.log('singlewireRes.data.sites.data:', singlewireRes.data.sites.data);
          setSinglewireSites(singlewireRes.data.sites.data);
        }
        
        // If no CMS is selected, set dataLoaded to true to show the message
        if (!selectedCms) {
          setDataLoaded(true);
        }
      })
      .catch((err) => {
        setError(<FormattedMessage id="FAILED_TO_FETCH_CMS_OPTIONS" defaultMessage="Failed to fetch CMS options"/>);
        setDataLoaded(true); // Set to true on error to show message
      });
  }, []); 

  // Create a memoized lookup map for O(1) Singlewire site access
  const singlewireSitesMap = useMemo(() => {
    const map = new Map();
    singlewireSites.forEach(site => {
      map.set(site.id, site);
    });
    return map;
  }, [singlewireSites]);

  // Separate function to handle displaying sites for selected CMS
  function displaySites(selectedCmsId) {
    setData([]);
    setSites([]);
    setDataCount(0);
    setTotalDataCount(0);
    
    // Find selected CMS from our cached data
    const selectedCms = allCmsData.find(cms => cms.id === selectedCmsId);
    
    if (!selectedCms) {
      setError(<FormattedMessage id="NO_CMS_SELECTED" defaultMessage="Please select a CMS"/>);
      setDataLoaded(true); // Set to true to show message
      return;
    }

    // Set the selected CMS
    setSelectedCms(selectedCms);
    
    // Reset search value when changing CMS
    if (searchValue) {
      setSearchValue('');
    }
    
    // Set dataLoaded to false while loading data
    setDataLoaded(false);
    
    loadTable(dataMap, true, selectedCmsId);
  }

  const loadTable = useCallback((map, resetSelection = true, selectedCmsId) => {
    // Store expansion state before clearing data
    const expansionState = {};
    if (!resetSelection) {
      // Save expansion state of all rows
      Object.keys(map.data).forEach(id => {
        if (map.data[id] && map.data[id].isExpanded) {
          expansionState[id] = true;
        }
      });
    }
    
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    
    if (resetSelection) {
      map.selectedIds = {};
    }
  
    if (!selectedCmsId) {
      setDataLoaded(true);
      return;
    }

    // Always fetch fresh data when loading table
    API.getCmses()
      .then((res) => {
        setAllCmsData(res.data);
        setCmses(res.data);
        const currentCms = res.data.find(cms => cms.id === selectedCmsId);
        
        if (!currentCms?.sites || currentCms.sites.length === 0) {
          setError(<FormattedMessage id="NO_SITES_AVAILABLE_FOR_SELECTED_CMS" defaultMessage="No sites available for the selected CMS"/>);
          setData([]);
          setSites([]);
          setDataCount(0);
          setTotalDataCount(0);
          setDataLoaded(true);
          return;
        }

        try {
          // Group sites by parent
          const sitesByParent = {};
          const rootSites = [];
          
          // First pass: create site objects and group by parent
          currentCms.sites.forEach(site => {
            console.log('site:', site);
            
            const siteObj = {
              id: site.id,
              name: site.name,
              singlewireSiteName: site.singlewire_site_name,
              status: site.singlewire_site_name ? 
                <FormattedMessage id="MAPPED" defaultMessage="Mapped"/> : 
                <FormattedMessage id="UNMAPPED" defaultMessage="Unmapped"/>,
              parentId: site.parent_id,
              children: [],
              // Preserve expansion state if this is a refresh
              isExpanded: !resetSelection && expansionState[site.id] ? true : false,
              checked: false
            };

            // Check for both null and null UUID
            if (site.parent_id && site.parent_id !== '00000000-0000-0000-0000-000000000000') {
              if (!sitesByParent[site.parent_id]) {
                sitesByParent[site.parent_id] = [];
              }
              sitesByParent[site.parent_id].push(siteObj);
            } else {
              rootSites.push(siteObj);
            }
          });

          // Second pass: attach children to parents
          const processChildren = (sites) => {
            sites.forEach(site => {
              if (sitesByParent[site.id]) {
                site.children = sitesByParent[site.id];
                // Recursively process children's children
                processChildren(site.children);
              }
            });
          };
          
          processChildren(rootSites);

          // Store in dataMap
          const storeInMap = (sites, level = 0) => {
            sites.forEach(site => {
              map.data[site.id] = {
                ...site,
                level,
                hasChildren: site.children.length > 0,
                style: {
                  ...styles.nestedRow(level),
                }
              };
              
              // Only root sites go into sortable IDs
              if (level === 0) {
                map.ids.push(site.id);
              }
              
              if (site.children.length > 0) {
                storeInMap(site.children, level + 1);
              }
            });
          };

          storeInMap(rootSites);
          
          // Initialize visible IDs with only root sites
          map.filteredIds = [...map.ids];
          map.sortedIds = [...map.ids];

          setSites(rootSites);
          
          // If we're refreshing (not resetting selection), restore expanded rows
          if (!resetSelection) {
            try {
              console.log('Restoring expansion state for rows:', Object.keys(expansionState));
              
              // Clear sorted and filtered IDs first to prevent duplicates
              map.sortedIds = [...map.ids];
              map.filteredIds = [...map.ids];
              
              // For each expanded row, add its children to the visible IDs
              Object.keys(expansionState).forEach(expandedId => {
                // Skip if the site no longer exists in the data
                if (!map.data[expandedId]) {
                  console.log('Site no longer exists:', expandedId);
                  return;
                }
                
                // Skip if the site doesn't have children
                if (!map.data[expandedId].hasChildren) {
                  console.log('Site has no children:', expandedId);
                  return;
                }
                
                // Ensure the row is marked as expanded
                map.data[expandedId].isExpanded = true;
                console.log('Marking site as expanded:', expandedId);
                
                // Add all children to visible IDs
                const addChildrenRecursively = (parentId) => {
                  // Safety check for parent
                  const parent = map.data[parentId];
                  if (!parent) {
                    console.log('Parent not found:', parentId);
                    return;
                  }
                  
                  // Safety check for children
                  if (!parent.children || !Array.isArray(parent.children)) {
                    console.log('Parent has no children array:', parentId);
                    return;
                  }
                  
                  // Get child IDs
                  const childIds = parent.children.map(child => child.id);
                  if (childIds.length === 0) {
                    console.log('Parent has empty children array:', parentId);
                    return;
                  }
                  
                  // Find parent index
                  const parentIndex = map.sortedIds.indexOf(parentId);
                  if (parentIndex === -1) {
                    console.log('Parent not found in sortedIds:', parentId);
                    return;
                  }
                  
                  // Insert children after their parent
                  console.log('Adding children to visible IDs:', childIds);
                  map.sortedIds.splice(parentIndex + 1, 0, ...childIds);
                  map.filteredIds.splice(parentIndex + 1, 0, ...childIds);
                  
                  // Recursively add children of expanded children
                  childIds.forEach(childId => {
                    const child = map.data[childId];
                    if (child && child.hasChildren && expansionState[childId]) {
                      addChildrenRecursively(childId);
                    }
                  });
                };
                
                // Start recursive process
                addChildrenRecursively(expandedId);
              });
            } catch (error) {
              console.error('Error restoring expansion state:', error);
            }
          }
          
          if (searchValue !== "") {
            tableRef.current.search(map);
          } else {
            sortMap(map);
            setData(generateDataSlice(map, 0));
            setDataCount(map.sortedIds.length);
          }

          setTotalDataCount(Object.keys(dataMap.data).length);
          setStartIndexForTable(0);
          setDataLoaded(true);
        } catch (error) {
          setError();
          setDataLoaded(true);
        }
      })
      .catch((err) => {
        setError(<FormattedMessage id="FAILED_TO_FETCH_CMS_OPTIONS" defaultMessage="Failed to fetch CMS options"/>);
      });
  }, [searchValue, tableRef, sortMap, generateDataSlice, setData, setDataCount, setTotalDataCount, setStartIndexForTable, setDataLoaded, setError, setSites, singlewireSites]);
  
  // Modified toggle expansion function
  const toggleRowExpansion = (rowId) => {
    const site = dataMap.data[rowId];
    // Add null check to prevent errors when site doesn't exist
    if (!site || !site.hasChildren) return;
  
    // Toggle expansion state
    site.isExpanded = !site.isExpanded;
    
    // If expanding, add all visible children (including children of expanded children)
    if (site.isExpanded) {
      const addChildrenRecursively = (parentId) => {
        const parent = dataMap.data[parentId];
        const childIds = parent.children.map(child => child.id);
        const parentIndex = dataMap.sortedIds.indexOf(parentId);
        
        // Insert children after their parent
        dataMap.sortedIds.splice(parentIndex + 1, 0, ...childIds);
        dataMap.filteredIds.splice(parentIndex + 1, 0, ...childIds);
        
        // Recursively add children of expanded children
        childIds.forEach(childId => {
          const child = dataMap.data[childId];
          if (child.hasChildren && child.isExpanded) {
            addChildrenRecursively(childId);
          }
        });
      };
      
      addChildrenRecursively(rowId);
    } else {
      // If collapsing, remove all descendants regardless of their expansion state
      dataMap.sortedIds = dataMap.sortedIds.filter(id => {
        const currentSite = dataMap.data[id];
        return !isDescendantOf(currentSite, site);
      });
      dataMap.filteredIds = [...dataMap.sortedIds];
    }
  
    // Update visible data
    setData(generateDataSlice(dataMap, startIndexForTable));
    setDataCount(dataMap.sortedIds.length);
  };
  
  // Helper function to check if a site is a descendant of another site
  function isDescendantOf(possibleDescendant, ancestor) {
    let current = possibleDescendant;
    while (current.parentId) {
      if (current.parentId === ancestor.id) return true;
      const parent = dataMap.data[current.parentId];
      // If parent doesn't exist, break the loop
      if (!parent) break;
      current = parent;
    }
    return false;
  }

  const getChildSites = useCallback(async (siteId) => {
    if (childSitesMap[siteId]) {
      return childSitesMap[siteId];
    }
    try {
      const children = await API.getChildSites(siteId, selectedCms.id);
      setChildSitesMap(prev => ({
        ...prev,
        [siteId]: children
      }));
      return children;
    } catch (error) {
      return [];
    }
  }, [selectedCms.id, childSitesMap]);

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, selectedCms]); // Note we add selectedCms here since that's your main data source

  useEffect(() => {
    const loadChildSites = async () => {
      if (!data) return;
      
      await Promise.all(
        data.map(site => getChildSites(site.id))
      );
    };

    loadChildSites();
  }, [data, getChildSites]);

  const deassignSinglewireSite = () => {
    const selectedSiteIds = Object.keys(dataMap.selectedIds);
    console.log('trying to deassign Singlewire sites', selectedSiteIds, selectedCms.id);
    
    API.deassignSinglewireSite(selectedSiteIds, selectedCms.id)
      .then(() => {
        setSuccess(<FormattedMessage id="SUCCESS_DEASSIGN_SINGLEWIRE_SITES" defaultMessage="Successfully deassigned Singlewire sites"/>);
        loadTable(dataMap, false, selectedCms.id);
      })
      .catch((err) => {
        setError(<FormattedMessage id="FAILED_TO_DEASSIGN_SINGLEWIRE_SITES" defaultMessage="Failed to deassign Singlewire sites"/>);
      });
  };

  const renderSelectDropdown = () => (
    <div className="btn-group cms-dropdown">
      <button
        type="button"
        className="btn dropdown-toggle"
        id="cmsDropdownBtn"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        {selectedCms ? selectedCms.name : <FormattedMessage id="SELECT_CMS" defaultMessage="Select CMS..." />}
      </button>
      <ul 
        className="dropdown-menu" 
        style={{ 
          maxHeight: '300px', 
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        {/* CMS options */}
        {cmses.map((cms) => (
          <li key={cms.id} id="siteItemBtn">
            <button
              className={`dropdown-item ${cms.id === selectedCms ? 'active' : ''}`}
              onClick={() => displaySites(cms.id)}
            >
              {cms.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  const styles = useMemo(() => ({
    tableRow: {
      position: 'relative',
    },
    nestedRow: (level) => ({
      position: 'relative',
      backgroundColor: level % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: `${level * 20}px`,
        top: 0,
        bottom: 0,
        width: '2px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        display: level > 0 ? 'block' : 'none',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: `${level * 20}px`,
        width: '12px',
        height: '2px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        top: '50%',
        display: level > 0 ? 'block' : 'none',
      }
    }),
    cellContent: (level) => ({
      paddingLeft: `${level * 24}px`,
      display: 'flex',
      alignItems: 'center',
    }),
    expandButton: {
      padding: '4px',
      marginRight: '8px',
      cursor: 'pointer',
      transition: 'transform 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      '&.expanded': {
        transform: 'rotate(90deg)',
      },
      '& img': {
        width: '16px',
        height: '16px',
      }
    },
  }), []);

  const tableColumns = useMemo(() => [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'SCREENBEAM_SITE_NAME',
      accessor: 'name',
      id: 'name',
      Cell: ({ row, value }) => {
        const level = row.original.level || 0;
        return (
          <div style={{
            ...styles.cellContent(level),
            // Add extra padding equal to expand button width + margin for children without the expand button
            paddingLeft: `${(level * 24) + (level > 0 && !row.original.hasChildren ? 32 : 0)}px`
          }}>
            {row.original.hasChildren && (
              <div 
                style={styles.expandButton} 
                className={row.original.isExpanded ? 'expanded' : ''}
                onClick={() => toggleRowExpansion(row.original.id)}
              >
                {row.original.isExpanded ? 
                  <img src={ChevronDown} alt="collapse" /> :
                  <img src={ChevronRight} alt="expand" />
                }
              </div>
            )}
            {value}
          </div>
        );
      },
      isVisible: true,
    },
    {
      Header: 'SINGLEWIRE_SITE_NAME',
      accessor: 'singlewireSiteName',
      id: 'singlewireSiteName',
      isVisible: true,
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      Cell: ({ value }) => {
        let statusString = value.props.defaultMessage.toLowerCase();
        return (
          <span 
            className={
              statusString === 'mapped' ?  'text-success' : 'text-danger'}
            style={ statusString === 'mapped' ? 
              { backgroundColor: 'rgba(0, 128, 0, 0.1)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' } :
              { backgroundColor: 'rgba(255, 0, 0, 0.1)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }
            }
          >
            {value}
          </span>
        )
      },
      isVisible: true,
    },
  ], [toggleRowExpansion, styles]);  

  const buttons = {
    footerButtons: (
      <>
        <button 
          type="button" 
          className="btn" 
          id="assignSinglewireSiteBtn"
          disabled={Object.keys(selectedIds).length === 0}
          data-bs-toggle="modal"
          data-bs-target="#assignBldgModal"
          onClick={null}>
          <FormattedMessage id="ASSIGN_SINGLEWIRE_SITES" defaultMessage="Assign Singlewire Sites" />
        </button>
        <button 
          type="button" 
          className="btn deleteBtn"
          id="deassignBuildingBtn"
          disabled={Object.keys(selectedIds).length === 0}
          onClick={deassignSinglewireSite}>
          <FormattedMessage id="DEASSIGN_SINGLEWIRE_SITES" defaultMessage="De-assign Singlewire Sites" />
        </button>
      </>
    ),
  }

  const tableProps = {
    pageType: constants.pageType.SiteMapping,
    modalId: null,
    columns: tableColumns,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
    selectionToolbar: renderSelectDropdown(),
    hasRowsWithChildren: true,
  };

  const assignBuildingModalProps = {
    cms: selectedCms,
    selectedSites: Object.keys(selectedIds),
    selectedCms,
    sites,
    dataMap,
    loadTable,
    setSuccess,
    singlewireSites,
    buildingName, setBuildingName,
    setData,
    generateDataSlice,
    startIndexForTable
  }

  return (
    <div>
      <Table {...tableProps} />
      <AssignBuildingModal {...assignBuildingModalProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(SiteMapping);
